import { coerceBooleanProperty } from "@angular/cdk/coercion";
import {
  AfterViewInit,
  ComponentFactory,
  ComponentFactoryResolver,
  ComponentRef,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef
} from "@angular/core";
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest as observableCombineLatest,
  from as observableFrom,
  of as observableOf
} from "rxjs";
import { distinctUntilChanged, skip, takeUntil } from "rxjs/operators";

import { BaseDefaultOptionLayouts } from "./base-default-option-layouts.component";
import { BaseOptionsDirective } from "./base-options.directive";
import { SelectClasses } from "./base-select-classes";
import {
  NewOptionLayoutDirective,
  OptionLayoutContext,
  OptionLayoutDirective,
  SelectedOptionLayoutDirective
} from "./option-layout.directive";
import {
  AsyncOptionsIterator,
  OptionsSource,
  OptionsSourceQuery
} from "./options-source";

@Directive()
export abstract class BaseSelectComponent<
  Model,
  Identifier extends number | string,
  FieldValue extends Model | Identifier
> implements OnInit, AfterViewInit, OnDestroy {
  // TODO: simplify this section somehow
  private defaultOptionLayoutsRef: ComponentRef<
    BaseDefaultOptionLayouts<Model>
  >;
  private get defaultOptionLayout(): OptionLayoutDirective<Model> {
    return this.defaultOptionLayoutsRef.instance.optionLayout;
  }
  private get defaultSelectedOptionLayout():
    | SelectedOptionLayoutDirective<Model>
    | undefined {
    return this.defaultOptionLayoutsRef.instance.selectedOptionLayout;
  }
  private get defaultNewOptionLayout():
    | NewOptionLayoutDirective<Model>
    | undefined {
    return this.defaultOptionLayoutsRef.instance.newOptionLayout;
  }

  @ContentChild(OptionLayoutDirective, { static: true })
  private contentOptionLayout?: OptionLayoutDirective<Model>;

  @ContentChild(SelectedOptionLayoutDirective, { static: true })
  private contentSelectedOptionLayout?: SelectedOptionLayoutDirective<Model>;

  get optionLayoutTemplate(): TemplateRef<OptionLayoutContext<Model>> {
    return this.contentOptionLayout != null
      ? this.contentOptionLayout.templateRef
      : this.defaultOptionLayout.templateRef;
  }

  get selectedOptionLayoutTemplate(): TemplateRef<OptionLayoutContext<Model>> {
    if (this.contentSelectedOptionLayout != null) {
      return this.contentSelectedOptionLayout.templateRef;
    }
    if (this.contentOptionLayout != null) {
      return this.contentOptionLayout.templateRef;
    }
    if (this.defaultSelectedOptionLayout != null) {
      return this.defaultSelectedOptionLayout.templateRef;
    }
    return this.defaultOptionLayout.templateRef;
  }

  get newOptionLayoutTemplate():
    | TemplateRef<OptionLayoutContext<Model>>
    | undefined {
    return (
      this.defaultNewOptionLayout && this.defaultNewOptionLayout.templateRef
    );
  }

  get queryToModel(): (query: string) => Model {
    return this.optionsSourceDirective.queryToModel!;
  }

  abstract value: FieldValue | FieldValue[] | undefined;

  @Input()
  valueBy: "id" | "model" = "id";

  @Input()
  get fetchByModel(): boolean {
    return this._fetchByModel;
  }

  set fetchByModel(value: boolean) {
    value = coerceBooleanProperty(value);
    if (this._fetchByModel === value) {
      return;
    }

    this._fetchByModel = value;
  }
  private _fetchByModel: boolean = false;

  // TODO: figure out why this is a bug? it seems having object type with the decorator throws an error on dev compile
  @Input()
  get filters(): {} | undefined {
    return this.filtersChange.value;
  }
  set filters(val: {} | undefined) {
    if (!!val !== !!this.filtersChange.value) {
      this.filtersChange.next(val);
      return;
    }
    if (val == null && this.filtersChange.value == null) {
      return;
    }
    const filtersChanged = [
      ...Object.keys(val!),
      ...Object.keys(this.filtersChange.value!)
    ].some((key) => val![key] !== this.filtersChange.value![key]);
    if (filtersChanged) {
      this.filtersChange.next(val);
    }
  }
  filtersChange: BehaviorSubject<{} | undefined> = new BehaviorSubject<
    {} | undefined
  >({});

  queryString: BehaviorSubject<string | undefined> = new BehaviorSubject<
    string | undefined
  >(undefined);

  loadedOptions: BehaviorSubject<Model[]> = new BehaviorSubject<Model[]>([]);
  loadingOptions: boolean = false;

  protected get optionsSource(): OptionsSource<Model> {
    return this.optionsSourceDirective.optionsSource;
  }
  protected optionsIterator?: AsyncOptionsIterator<Model>;
  protected optionsDone: boolean = true;

  abstract setSelected(model?: Model | Model[]): void;
  abstract isSelected(model: Model): boolean;

  @HostListener("focus", ["$event"])
  onFocusInteraction(event: Event): void {
    this.touched = true;
  }

  get touched(): boolean {
    return this._touched;
  }
  set touched(value: boolean) {
    value = value != null && value !== false;
    if (this._touched === value) {
      return;
    }

    this._touched = value;
    if (this._touched) {
      this.addHostClass(this.selectClasses.parentTouched);
    }
    this.touchedChange.emit(value);
  }
  private _touched: boolean;
  @Output() touchedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    protected injector: Injector,
    private optionsSourceDirective: BaseOptionsDirective<Model, Identifier>,
    public selectClasses: SelectClasses,
    public elementRef: ElementRef,
    protected renderer: Renderer2
  ) {}

  ngOnInit(): void {
    let resolver: ComponentFactoryResolver = this.injector.get(
        ComponentFactoryResolver
      ),
      factory: ComponentFactory<BaseDefaultOptionLayouts<
        Model
      >> = resolver.resolveComponentFactory(
        this.optionsSourceDirective.defaultOptionLayoutsComponent
      );
    this.defaultOptionLayoutsRef = factory.create(this.injector);
    this.addHostClass(this.selectClasses.parent);
    this.initParamsSubscription();
    this.initControlAccessors();
  }

  protected initParamsSubscription(): void {
    this.initOptionsSourceQueryChangeSubscription();
  }

  private initOptionsSourceQueryChangeSubscription(): void {
    observableCombineLatest([this.filtersChange, this.queryString])
      .pipe(
        skip(1), // ignore initial empty vals. initializing options is handled in OnInit.
        distinctUntilChanged(([filters1, query1], [filters2, query2]) => {
          return filters1 === filters2 && query1 === query2;
        }),
        takeUntil(this.onDestroyEmitter)
      )
      .subscribe(([filters, queryString]) => {
        this.getOptions({ search: queryString, filters });
      });
  }

  ngAfterViewInit(): void {
    this.optionsSourceDirective.optionsSourceChange
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(() => {
        this.getOptions({
          search: this.queryString.value,
          filters: this.filtersChange.value
        });
      });
  }

  protected onDestroyEmitter = new EventEmitter<void>();
  ngOnDestroy(): void {
    this.defaultOptionLayoutsRef.destroy();

    this.onDestroyEmitter.emit();
    this.onDestroyEmitter.complete();
  }

  getModelsFromIdentifiers(identifiers: Identifier[]): Observable<Model[]> {
    return this.optionsSourceDirective.getModelsFromIdentifiers(identifiers);
  }
  getIdentifierFromModel(model: Model): Identifier {
    return this.optionsSourceDirective.getIdentifierFromModel(model);
  }

  getModelsFromFieldValues(fieldValues: FieldValue[]): Observable<Model[]> {
    if (this.valueBy === "model" && !this.fetchByModel) {
      return observableOf(fieldValues as Model[]);
    }
    if (this.valueBy === "model") {
      return this.getModelsFromIdentifiers(
        fieldValues.map((fieldValue) =>
          this.getIdentifierFromFieldValue(fieldValue)
        )
      );
    }
    return this.getModelsFromIdentifiers(fieldValues as Identifier[]);
  }

  getIdentifierFromFieldValue(fieldValue: FieldValue): Identifier {
    if (this.valueBy === "model") {
      return this.getIdentifierFromModel(fieldValue as Model);
    }
    return fieldValue as Identifier;
  }

  getOptions(query: OptionsSourceQuery = {}): void {
    query.filters = Object.assign({}, this.filters, query.filters);

    let source:
      | Model[]
      | Promise<Model[]>
      | AsyncOptionsIterator<Model> = this.optionsSource(query);
    if (source instanceof Array) {
      this.getOptionsArray(source);
    } else if (source instanceof Promise) {
      this.getAsyncOptions(source);
    } else {
      this.getAsyncOptionsIterator(source);
    }
  }

  private getOptionsArray(models: Model[]): void {
    // for congruency with optionsIterator process
    this.loadingOptions = false;
    this.optionsDone = true;
    this.optionsIterator = undefined;

    this.loadedOptions.next(models);
  }

  private loadingOptionsSubscription?: Subscription;
  private getAsyncOptions(asyncOptions: Promise<Model[]>): void {
    if (this.loadingOptionsSubscription != null) {
      this.loadingOptionsSubscription.unsubscribe();
    }

    // for congruency with optionsIterator process
    this.loadingOptions = true;
    this.optionsDone = false;
    this.optionsIterator = undefined;

    // set/reset options to empty array
    this.loadedOptions.next([]);

    this.loadingOptionsSubscription = observableFrom(asyncOptions).subscribe(
      (res) => {
        this.loadingOptions = false;
        this.optionsDone = true;
        this.loadedOptions.next(res);
      }
    );
  }

  private getAsyncOptionsIterator(iterator: AsyncOptionsIterator<Model>): void {
    this.loadingOptions = false;
    this.optionsDone = false;
    this.optionsIterator = iterator;

    // set/reset options to empty array
    this.loadedOptions.next([]);

    this.getNextOptionsPage();
  }

  protected addHostClass(className: string): void {
    this.renderer.addClass(this.elementRef.nativeElement, className);
  }

  protected removeHostClass(className: string): void {
    this.renderer.removeClass(this.elementRef.nativeElement, className);
  }

  getNextOptionsPage(): void {
    // called when there's an options generator
    // use directly when you want "more of the same"--e.g., infinite scroll
    // use initDisplayOptions for a fresh set of options--e.g., on query change
    if (this.loadingOptions) {
      return; // before attempting to unsubscribe
    }
    if (this.optionsDone || this.optionsIterator == null) {
      return;
    }
    if (this.loadingOptionsSubscription != null) {
      this.loadingOptionsSubscription.unsubscribe();
    }

    this.loadingOptions = true;
    let asyncPromise = this.optionsIterator.next();
    this.loadingOptionsSubscription = observableFrom(asyncPromise).subscribe(
      (res) => {
        this.optionsDone = res.done;

        let loadedOptions: Model[] = this.loadedOptions.value;
        loadedOptions.push.apply(loadedOptions, res.value);

        this.loadedOptions.next(loadedOptions);
        this.loadingOptions = false;
        this.loadingOptionsSubscription = undefined;
      },
      () => {
        // TODO: show message about failed to load options
        this.loadingOptions = false;
        this.loadingOptionsSubscription = undefined;
      }
    );
  }

  protected initializedModel: boolean = false;
  initControlAccessors(): void {
    const touchSubscription = this.touchedChange
      .pipe(takeUntil(this.onDestroyEmitter))
      .subscribe(() => {
        if (this._onTouchedFn != null) {
          this._onTouchedFn();
        }
        touchSubscription.unsubscribe();
      });
  }

  writeValue(value: any): void {
    this.value = value;
  }

  protected _onChangeFn?: (value: any) => void;
  registerOnChange(fn: any): void {
    this._onChangeFn = fn;
  }

  private _onTouchedFn?: () => void;
  registerOnTouched(fn: any): void {
    this._onTouchedFn = fn;
  }
}
